import { useCallback, useMemo } from 'react';
import Head from 'next/head';
import { useQueryClient } from '@tanstack/react-query';
import { ObjectBrowser } from '@/components/ObjectBrowser';
import type { BrowserFetcherResult, BrowserFilterConfig } from '@/components/ObjectBrowser';
import { InitiativeCard } from '@/components/initiatives/InitiativeCard';
import { initiativesApi } from '@/lib/api';
import type { ApiInitiative, InitiativesQuery } from '@/lib/api/types';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { INITIATIVE_TYPE_CONFIG } from '@sizzlenz/shared';

const FILTER_CONFIGS: BrowserFilterConfig[] = [
  {
    key: 'type',
    label: 'Type',
    options: Object.entries(INITIATIVE_TYPE_CONFIG)
      .filter(([key]) => key !== 'quiz')
      .map(([key, config]) => ({ id: key, label: config.label })),
    displayMode: 'combobox',
  },
];

const URL_SYNC_FILTERS = {
  search: { paramName: 'search', defaultValue: '' },
  type: { paramName: 'type', defaultValue: [] as string[], isArray: true as const },
};

export default function InitiativeStartersPage() {
  const queryClient = useQueryClient();

  const { values, handlers, isReady } = useUrlSync({
    filters: URL_SYNC_FILTERS,
  });

  const initialFilters = useMemo(() => {
    const f: Record<string, string[]> = {};
    if (values.type.length > 0) f.type = values.type;
    return f;
  }, [values.type.join(',')]);

  const fetcher = useCallback(
    async (params: {
      search?: string;
      filters: Record<string, string[]>;
      cursor?: string;
      limit: number;
    }): Promise<BrowserFetcherResult<ApiInitiative>> => {
      const types = params.filters.type || [];

      const query: InitiativesQuery = {
        search: params.search,
        type: types.length > 0 ? types : undefined,
        isTemplate: true,
        visibility: 'public',
        status: 'active',
        limit: params.limit,
        nextCursor: params.cursor || undefined,
      };

      const response = await queryClient.fetchQuery({
        queryKey: ['initiatives', 'starters', query],
        queryFn: () => initiativesApi.getInitiatives(query),
        staleTime: 1000 * 10,
      });

      return {
        objects: response.initiatives,
        hasNextPage: !!response.pagination?.nextCursor,
        nextCursor: response.pagination?.nextCursor ?? undefined,
      };
    },
    [queryClient]
  );

  const handleSearchChange = useCallback(
    (search: string) => handlers.search(search),
    [handlers.search]
  );

  const handleFiltersChange = useCallback(
    (filters: Record<string, string[]>) => {
      handlers.type(filters.type || []);
    },
    [handlers.type]
  );

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Initiative Starters | Sizzlenz</title>
        <meta
          name="description"
          content="Browse initiative templates and starters to kickstart your next activation."
        />
      </Head>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2">
          <div className="mb-2">
            <h1 className="text-2xl font-bold text-gray-900">Initiative Starters</h1>
            <p className="text-sm text-gray-500 mt-1">
              Browse ready-to-use initiative templates. Clone and customise for your next
              activation.
            </p>
          </div>

          <ObjectBrowser<ApiInitiative>
            fetcher={fetcher}
            renderCard={(initiative) => (
              <InitiativeCard initiative={initiative} variant="template" />
            )}
            getKey={(initiative) => initiative.id}
            filters={FILTER_CONFIGS}
            searchEnabled
            searchPlaceholder="Search starters..."
            limit={40}
            emptyState={{
              title: 'No starters found',
              description: 'Try adjusting your search or filters',
            }}
            initialSearch={values.search}
            initialFilters={initialFilters}
            onSearchChange={handleSearchChange}
            onFiltersChange={handleFiltersChange}
          />
        </div>
      </div>
    </>
  );
}
